import styled, { css } from 'styled-components';

import {
    Button as BaseButton,
    Checkbox as BaseCheckbox,
    Radio as BaseRadio,
    Combobox as BaseCombobox
} from '@ninox/ninox-components';
import {
    BorderRadius,
    BorderWidth,
    FontFamily,
    FontSize,
    FontWeight,
    Size,
    Spacing
} from '@ninox/ninox-components/theme';

import GoogleImg from '../assets/google-logo.svg';

export const Backdrop = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100%;
    pointer-events: none;
`;

export const Title = styled.div`
    font-size: ${FontSize.xxx_large};
    font-weight: ${FontWeight.medium};
    line-height: 1.5;
    text-align: center;
`;

export const Button = styled(BaseButton)`
    width: 100%;
    justify-content: center;
    text-align: center;
    line-height: 2;
    border-radius: ${BorderRadius.largest};

    &:last-child {
        margin-bottom: ${Spacing.x_small};
    }
`;

export const FormControl = styled.div<{ required?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${Spacing.x_small};

    &:disabled {
        opacity: 0.5;
    }

    & > label {
        font-size: ${FontSize.large};
        color: ${({ theme }) => theme.text.label};
        line-height: 1.5;
    }

    ${({ required }) =>
        required &&
        css`
            & > label::after {
                content: '*';
                color: ${({ theme }) => theme.text.invalid};
                padding-left: ${Spacing.xxx_small};
            }
        `}
`;

export const Input = styled.input`
    box-sizing: border-box;
    width: 100%;
    padding: ${Spacing.small} ${Spacing.medium};
    line-height: 2;

    border-width: ${BorderWidth.tiny};
    border-style: solid;
    border-radius: ${BorderRadius.medium};
    outline: none;

    font-family: ${FontFamily.sans};
    font-weight: ${FontWeight.normal};
    font-size: ${FontSize.x_large};

    ${({ theme }) => theme.input.default}
    border-color: ${({ theme }) => theme.input.hover.borderColor};

    &[aria-invalid='true'] {
        ${({ theme }) => theme.input.invalid}
    }

    &:hover {
        ${({ theme }) => theme.input.hover}
    }

    &:focus {
        ${({ theme }) => theme.input.focus}
    }

    &::placeholder {
        opacity: 1;
        ${({ theme }) => theme.input.placeholder};
    }

    &:autofill {
        background-color: ${({ theme }) => theme.surface.accent} !important;
    }
`;

export const Radio = styled(BaseRadio)`
    box-sizing: border-box;
    width: 100%;
    padding: ${Spacing.medium};
    padding-left: calc(2 * ${Spacing.medium});

    gap: ${Spacing.medium};
    line-height: 2 !important;

    color: ${({ theme }) => theme.text.secondary} !important;

    border-color: ${({ theme }) => theme.input.hover.borderColor};
    border-width: ${BorderWidth.tiny};
    border-style: solid;
    border-radius: ${BorderRadius.large};
    outline: none;

    font-size: ${FontSize.x_large} !important;
    font-weight: ${FontWeight.semibold};

    :checked {
        color: ${({ theme }) => theme.text.primary} !important;
    }
`;

export const Checkbox = styled(BaseCheckbox)`
    box-sizing: border-box;
    width: 100%;
    padding: ${Spacing.small} ${Spacing.large};

    gap: ${Spacing.large};
    line-height: 1.5 !important;

    border-color: ${({ theme }) => theme.input.hover.borderColor};
    border-width: ${BorderWidth.tiny};
    border-style: solid;
    border-radius: ${BorderRadius.large};
    outline: none;

    font-size: ${FontSize.small} !important;
`;

export const Combobox = styled(BaseCombobox)`
    flex: 1 0 auto;
    height: fit-content;
    width: 100%;
    padding: ${Spacing.medium};

    font-weight: ${FontWeight.normal};
    font-size: ${FontSize.x_large};

    border-color: ${({ theme }) => theme.input.hover.borderColor};
    border-width: ${BorderWidth.tiny};
    border-style: solid;
    outline: none;

    &[aria-invalid='true'] {
        border-color: ${({ theme }) => theme.input.hover.borderColor};
    }
`;

export const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing.xx_small};

    color: ${({ theme }) => theme.text.invalid};

    font-size: ${FontSize.medium};
    font-weight: ${FontWeight.medium};
`;

export const FormErrorMessage = styled.div`
    color: ${({ theme }) => theme.text.invalid};
    font-size: ${FontSize.medium};
    font-weight: ${FontWeight.medium};
    text-align: center;
`;

export const GoogleButton = styled(Button)`
    line-height: 3;
    gap: ${Spacing.medium};
    border-color: ${({ theme }) => theme.grid.accent} !important;
    border-radius: ${BorderRadius.small};
`;

export const GoogleLogo = styled.div`
    align-self: center;
    background: url(${GoogleImg}) no-repeat center center;
    background-size: contain;

    height: ${Size.sm};
    width: ${Size.sm};
`;

export const Separator = styled.div`
    width: 100%;
`;

export const Lines = styled.div`
    display: flex;
    align-items: center;
    user-select: none;

    &::after,
    &::before {
        content: '';
        flex: 1 1 auto;
        height: ${BorderWidth.tiny};
        background-color: ${({ theme }) => theme.grid.secondary};
    }
`;

export const Text = styled.div`
    color: ${({ theme }) => theme.text.label};
    font-size: ${FontSize.large};
    padding: 0 ${Spacing.medium};
`;

export const Link = styled.a`
    cursor: pointer;
    display: inline-block;

    color: ${({ theme }) => theme.text.accent};
    font-size: ${FontSize.x_large};
    font-weight: ${FontWeight.medium};
    line-height: 1.5;
    overflow-wrap: anywhere;
    text-decoration: none;

    &:visited {
        color: ${({ theme }) => theme.text.accent};
    }
`;

export const SwitchPage = styled.div`
    display: flex;
    gap: ${Spacing.x_small};
    padding: ${Spacing.medium};
    line-height: 1.5;

    color: ${({ theme }) => theme.text.label};

    font-size: ${FontSize.large};
    font-weight: ${FontWeight.light};

    & > ${Link} {
        font-size: ${FontSize.large};
        white-space: nowrap;
    }
`;
